import type { EnrichedExecutionStop } from '../../../executionOperations/types';
import type { StopOperationalContext } from '../operationalContextFacade';
import { formatCargoLinesList } from '../operationalContextFacade';
import { getExecutionStatusLabel, normalizeOperationalState } from '../statusFacade';
import { isLoadingStop } from '../../../executionOperations/executionHelpers';
import type { RouteFlowProjection } from './types';
import {
  formatStopDestinationFromContext,
  type RouteStopPresentation
} from '../../routeSummaryProjection';

function shortCargoSummary(ctx: StopOperationalContext | undefined): string {
  if (!ctx || ctx.cargoLines.length === 0) return '—';
  const lines = formatCargoLinesList(ctx.cargoLines.slice(0, 2));
  if (ctx.cargoLines.length > 2) {
    return `${lines.join('; ')} +${ctx.cargoLines.length - 2}`;
  }
  return lines.join('; ');
}

export function buildRouteFlowProjection(params: {
  enrichedStops: EnrichedExecutionStop[];
  stopContextMap: Map<string, StopOperationalContext>;
  activeExecutionStopUid: string | null;
  presentationByStopId?: Map<number, RouteStopPresentation>;
}): RouteFlowProjection {
  const { enrichedStops, stopContextMap, activeExecutionStopUid, presentationByStopId } = params;

  const stops = enrichedStops.map((stop, index) => {
    const ctx = stopContextMap.get(stop.executionStopUid);
    const isLoading = isLoadingStop(stop.type);
    const state = normalizeOperationalState(stop.executionState);

    const seq = stop.position != null ? Number(stop.position) + 1 : index + 1;
    const presentation = presentationByStopId?.get(stop.stopId);
    const destinationLabel =
      presentation?.destinationLabel ??
      formatStopDestinationFromContext({
        pointType: stop.pointType,
        warehouseName: ctx?.warehouseName,
        consigneeName: ctx?.consigneeName,
        applicationLabel: stop.pointType === 'application' ? ctx?.applicationLabel : null,
        clientName: ctx?.clientName,
        city: stop.city,
        address: stop.address
      });

    return {
      stopId: stop.stopId,
      executionStopUid: stop.executionStopUid,
      type: isLoading ? ('loading' as const) : ('unloading' as const),
      label: stop.label ?? `${isLoading ? 'Погрузка' : 'Выгрузка'} #${seq}`,
      destinationLabel,
      state,
      stateLabel: getExecutionStatusLabel(state),
      address: stop.address,
      plannedAt: stop.plannedAt ?? ctx?.plannedAt ?? null,
      actualAt: ctx?.actualAt ?? null,
      applicationNumber: ctx?.applicationNumbers[0] ?? null,
      clientName: ctx?.clientName ?? null,
      consigneeName: ctx?.consigneeName ?? null,
      shortCargoSummary: shortCargoSummary(ctx),
      isActive: stop.executionStopUid === activeExecutionStopUid,
      isCompleted: stop.isCompleted,
      progressPercent: stop.completionPercent
    };
  });

  return { stops };
}
